/**
 * Разбор отказа продукта — единая точка приёма.
 *
 * Норма: spec/invariants/api-errors.md
 *  - api-errors-R06/R07 — отказ несёт машиночитаемый признак причины (`code`),
 *    параметры подстановки (`params`) и запасное сообщение (`message`);
 *    признак и параметры — самостоятельные части, а не текст.
 *  - api-errors-R08 — отказ доходит до пользователя: подмена причины отказа
 *    обобщённым текстом («запрос не удался») недопустима.
 *
 * Поэтому здесь тело, сформированное продуктом, ПЕРЕНОСИТСЯ как есть.
 * Никакие константы вместо него не подставляются: код и параметры остаются
 * ровно теми, что прислал продукт, а обобщённый признак назначается только
 * когда продукт не прислал ничего (сетевой сбой, пустое тело).
 */

import type { AxiosResponse } from "axios";
import type { IApiError, IApiResponse } from "@/shared/types";

/** Признак причины, когда продукт не прислал своего. */
export const UNKNOWN_ERROR_CODE = "UNKNOWN_ERROR";

/** Признак причины, когда ответа не было вовсе (сеть, таймаут, отмена запроса). */
export const NETWORK_ERROR_CODE = "NETWORK_ERROR";

/** Форма отказа, приходящая от продукта. */
interface IFailureBody {
  readonly code?: unknown;
  readonly message?: unknown;
  readonly error?: unknown;
  readonly params?: unknown;
  readonly details?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asParams(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) && !Array.isArray(value) ? value : undefined;
}

/** Признак причины, выведенный из статуса ответа, когда продукт своего не прислал. */
function codeFromStatus(status: number | undefined): string {
  return status === undefined ? UNKNOWN_ERROR_CODE : `HTTP_${String(status)}`;
}

/**
 * Читает отказ из тела ответа продукта.
 * Возвращает `undefined`, если тело не похоже на отказ, — тогда причину
 * приходится выводить из статуса.
 */
function fromBody(body: unknown, status: number | undefined): IApiError | undefined {
  if (isRecord(body) && !Array.isArray(body)) {
    const failure = body as IFailureBody;
    const code = asNonEmptyString(failure.code);
    const message =
      asNonEmptyString(failure.message) ?? asNonEmptyString(failure.error);

    // Тело считается отказом продукта, только если несёт признак причины
    // либо сообщение. Иначе это не отказ, а какой-то другой JSON.
    if (code !== undefined || message !== undefined) {
      const params = asParams(failure.params);
      const details = asParams(failure.details);
      return {
        code: code ?? codeFromStatus(status),
        message: message ?? "",
        ...(params !== undefined ? { params } : {}),
        ...(details !== undefined ? { details } : {}),
      };
    }
    return undefined;
  }

  // Ответ простым текстом (устаревшая форма) — сохраняем как запасное сообщение.
  const text = asNonEmptyString(body);
  if (text !== undefined) {
    return { code: codeFromStatus(status), message: text };
  }

  return undefined;
}

/**
 * Приводит произвольный сбой (исключение axios, ошибка сети, брошенное значение)
 * к отказу в форме `IApiError`, СОХРАНЯЯ причину и параметры, если продукт их прислал.
 */
export function toApiError(error: unknown): IApiError {
  if (isRecord(error) && "response" in error && isRecord(error["response"])) {
    const response = error["response"];
    const status =
      typeof response["status"] === "number" ? response["status"] : undefined;

    const fromProduct = fromBody(response["data"], status);
    if (fromProduct !== undefined) {
      return fromProduct;
    }

    return {
      code: codeFromStatus(status),
      message: error instanceof Error ? error.message : String(error),
    };
  }

  // Запроса не случилось или ответа не было — сетевой сбой, а не отказ продукта.
  if (isRecord(error) && error["isAxiosError"] === true) {
    return {
      code: NETWORK_ERROR_CODE,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  if (error instanceof Error) {
    return { code: UNKNOWN_ERROR_CODE, message: error.message };
  }

  return { code: UNKNOWN_ERROR_CODE, message: String(error) };
}

/**
 * Читает отказ из ответа, полученного БЕЗ выброса исключения
 * (когда клиент настроен не бросать на не-2xx).
 */
export function apiErrorFromResponse(response: AxiosResponse<unknown>): IApiError {
  return (
    fromBody(response.data, response.status) ?? {
      code: codeFromStatus(response.status),
      message: response.statusText,
    }
  );
}

/**
 * Оборачивает запрос в `IApiResponse`, донося отказ продукта до вызывающего.
 *
 * Принимает именно ПРОМИС запроса: axios отклоняет промис на не-2xx, и только
 * так тело отказа можно перехватить, а не потерять.
 */
export async function wrapApiResponse<T, R = T>(
  request: Promise<AxiosResponse<T>>,
  normalize?: (data: T) => R
): Promise<IApiResponse<R>> {
  try {
    const response = await request;
    const success = response.status >= 200 && response.status < 300;
    if (!success) {
      return { success: false, data: undefined, error: apiErrorFromResponse(response) };
    }
    return {
      success: true,
      data:
        normalize !== undefined
          ? normalize(response.data)
          : (response.data as unknown as R),
    };
  } catch (error) {
    return { success: false, data: undefined, error: toApiError(error) };
  }
}

/** То же для ответов без полезной нагрузки. */
export async function wrapApiVoidResponse(
  request: Promise<AxiosResponse<unknown>>
): Promise<IApiResponse<void>> {
  try {
    const response = await request;
    const success = response.status >= 200 && response.status < 300;
    return {
      success,
      data: undefined,
      error: success ? undefined : apiErrorFromResponse(response),
    };
  } catch (error) {
    return { success: false, data: undefined, error: toApiError(error) };
  }
}
